import React from 'react';
import { FONT } from '../constants';
import { C } from '../styles';
import type { DrawerMode } from './compensationHelpers';

interface Props {
  item: any;
  kind: DrawerMode;
  isLast: boolean;
  openMenuId: string | number | null;
  onToggleMenu: (id: string | number, e: React.MouseEvent) => void;
  onEdit: () => void;
  onPauseToggle: () => void;
  onRemove: () => void;
}

export default function CompensationItemRow({
  item, kind, isLast, openMenuId, onToggleMenu, onEdit, onPauseToggle, onRemove,
}: Props) {
  const isPaused = item.is_active === false;
  const isEarning = kind === 'earning';
  const typeObj = isEarning ? (item.pay_type || {}) : (item.deduction_type || {});

  const value = isEarning
    ? (item.rate_override != null ? item.rate_override : typeObj.default_rate)
    : (item.amount_override != null ? item.amount_override : typeObj.default_amount);
  const unit = isEarning
    ? (item.unit_label_override || typeObj.unit_label || '')
    : (typeObj.unit_label || '');
  const valueDisplay = value != null
    ? Number(value).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : 'Not set';

  // Earning metadata: taxable flags + description
  let metaJsx: React.ReactNode = null;
  let markerColor = (C as any).faint;

  if (isEarning) {
    const flags: string[] = [];
    if (typeObj.federal_taxable) flags.push('Federal');
    if (typeObj.cpp_contributable) flags.push('CPP');
    if (typeObj.ei_insurable) flags.push('EI');
    if (typeObj.vacationable) flags.push('Vacation');
    markerColor = flags.length ? (C as any).green : (C as any).faint;
    const taxSum = flags.length ? flags.join(', ') : 'Non-taxable';
    metaJsx = (
      <div style={{ fontSize: 11.5, color: (C as any).muted, lineHeight: 1.5 }}>
        {typeObj.description ? typeObj.description + ' . ' : ''}{taxSum}
      </div>
    );
  } else {
    markerColor = (C as any).amber;
    metaJsx = (
      <div style={{ fontSize: 11.5, color: (C as any).muted, lineHeight: 1.5, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {typeObj.is_pre_tax ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 600, color: C.tealInk, background: C.tealSoft, padding: '2px 7px', borderRadius: 4 }}>Pre-tax</span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 600, color: (C as any).muted, background: (C as any).surface, padding: '2px 7px', borderRadius: 4 }}>Post-tax</span>
        )}
        {typeObj.employer_matched && (
          <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 600, color: (C as any).green, background: (C as any).greenSoft, padding: '2px 7px', borderRadius: 4 }}>Employer match</span>
        )}
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 200px 44px',
      gap: 18, padding: '13px 18px',
      borderBottom: isLast ? 'none' : '1px solid ' + (C as any).lineSoft,
      alignItems: 'center', background: '#fff',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, opacity: isPaused ? 0.55 : 1 }}>
        <div style={{ width: 8, height: 8, borderRadius: 2, background: markerColor, flex: '0 0 8px' }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: C.ink, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{typeObj.name || 'Unknown'}</span>
            {isPaused && (
              <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10, fontWeight: 600, color: (C as any).amber, background: (C as any).amberSoft, padding: '1px 6px', borderRadius: 4 }}>Paused</span>
            )}
          </div>
          {metaJsx}
        </div>
      </div>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontVariantNumeric: 'tabular-nums',
        fontSize: 13.5, color: C.ink, fontWeight: 500,
        textAlign: 'right', opacity: isPaused ? 0.55 : 1,
      }}>
        {valueDisplay}
        {unit && <span style={{ fontFamily: FONT, color: (C as any).muted, fontWeight: 400, fontSize: 11, marginLeft: 2 }}> {unit}</span>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleMenu(item.id, e); }}
          style={{
            background: openMenuId === item.id ? (C as any).surface : 'none',
            border: '1px solid transparent', borderRadius: 5,
            width: 28, height: 28, cursor: 'pointer',
            color: (C as any).muted, display: 'grid', placeItems: 'center',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.4" />
            <circle cx="12" cy="12" r="1.4" />
            <circle cx="12" cy="19" r="1.4" />
          </svg>
        </button>
        {openMenuId === item.id && (
          <div style={{
            position: 'absolute', top: 32, right: 0,
            background: '#fff', border: '1px solid ' + C.line,
            borderRadius: 8, boxShadow: '0 4px 12px rgba(14,26,31,0.08)',
            zIndex: 10, minWidth: 160, overflow: 'hidden',
          }}>
            <div onClick={(e) => { e.stopPropagation(); onEdit(); }} style={{ padding: '9px 14px', fontSize: 13, color: C.tealInk, cursor: 'pointer', fontWeight: 500 }}>
              {isEarning ? 'Edit rate' : 'Edit amount'}
            </div>
            <div onClick={(e) => { e.stopPropagation(); onPauseToggle(); }} style={{ padding: '9px 14px', fontSize: 13, color: isPaused ? (C as any).green : (C as any).amber, cursor: 'pointer', fontWeight: 500 }}>
              {isPaused ? 'Resume' : 'Pause'}
            </div>
            <div onClick={(e) => { e.stopPropagation(); onRemove(); }} style={{ padding: '9px 14px', fontSize: 13, color: (C as any).err, cursor: 'pointer', borderTop: '1px solid ' + (C as any).lineSoft, fontWeight: 500 }}>
              Remove
            </div>
          </div>
        )}
      </div>
    </div>
  );
}